import Phaser from 'phaser';
import { TUNING } from '../config/tuning.js';
import { agingAt, byAging } from '../core/life.js';
import type { WorldScale } from '../core/WorldScale.js';
import { PALETTE } from '../ui/style.js';

const T = TUNING.bird;
const A = T.anim;
const L = TUNING.life;

/** Frames of the fox spritesheets (order: scripts/build-fox-sprites.py, FRAMES). */
export const PLAYER_FRAME = {
  /** flight, legs paddling */
  float: [0, 1],
  /** legs pulled up: right after a tap and while rising fast */
  hop: 1,
  /** surprised, while falling fast */
  fall: 2,
  hit: 3,
  joy: 4,
  /** at the end of a full life, on the ground */
  cheer: 5,
  /** front view (not used in the game – the controller shows it) */
  front: 6,
} as const;

export type Reaction = 'hit' | 'joy';

/**
 * The player character: a fox that hovers and gets an upward impulse with every tap.
 * Movement is integrated manually (no Arcade body needed): one vertical velocity, gravity,
 * an impulse per tap. Collision is a circle test against the pipe rectangles (see
 * PipeField) – cheap and deterministic.
 *
 * Animation: the spritesheet frame follows the movement – flight loop while idle or gliding,
 * hop (legs pulled up) after a tap and while going up fast, surprised while falling fast.
 * Reactions show for a moment on top of that: impact after a hit, joy after a coin; at the end
 * of a full life the fox cheers on the ground.
 */
export class Bird extends Phaser.GameObjects.Image {
  /** vertical velocity in world units per second (positive = down) */
  private vy = 0;
  private flying = false;
  /** 1 = young … `life.agilityOld` = old */
  private agility = 1;
  private blinkSec = 0;
  private look = 0;
  private frameIndex: number = PLAYER_FRAME.float[0];
  private animTime = 0;
  private kickSec = 0;
  private reaction: Reaction | null = null;
  private reactionSec = 0;
  private cheering = false;
  private bobTime = 0;
  private baseY = 0;

  constructor(
    scene: Phaser.Scene,
    private readonly ws: WorldScale,
  ) {
    super(scene, 0, 0, L.looks[0].texture, PLAYER_FRAME.float[0]);
    this.setDisplaySize(ws.px(T.displayWidth), ws.px(T.displayHeight));
    scene.add.existing(this);
  }

  /** radius of each collision circle in pixels */
  get hitRadius(): number {
    return this.ws.px(T.hitRadius);
  }

  /** Calls `test` with the centre (pixels) of every collision circle; true as soon as one hits. */
  anyHit(test: (x: number, y: number, r: number) => boolean): boolean {
    const r = this.hitRadius;
    return T.hitOffsets.some((dy) => test(this.x, this.y + this.ws.px(dy), r));
  }

  /** vertical velocity in world units per second (positive = down) */
  get velocityY(): number {
    return this.vy;
  }

  /** pixels from the centre down to the soles */
  get footOffset(): number {
    return this.ws.px(T.footOffset);
  }

  /** Place the player at its start position, young and hovering (no gravity). */
  reset(x: number, y: number): void {
    this.scene.tweens.killTweensOf(this);
    this.setPosition(x, y).setAngle(0).setAlpha(1);
    this.baseY = y;
    this.vy = 0;
    this.flying = false;
    this.blinkSec = 0;
    this.kickSec = 0;
    this.reaction = null;
    this.reactionSec = 0;
    this.cheering = false;
    this.animTime = 0;
    this.bobTime = 0;
    this.look = -1;
    this.setLifeProgress(0);
    this.showFrame(PLAYER_FRAME.float[0]);
  }

  /** Enable gravity (run starts without a tap). */
  launch(): void {
    this.flying = true;
  }

  /** Upward impulse. */
  flap(): void {
    this.flying = true;
    this.vy = -T.flapVelocity * this.agility;
    this.kickSec = A.kickSec;
    if (!this.reaction) this.showFrame(PLAYER_FRAME.hop);
    this.puff();
  }

  /** Stop all motion (after the end the player just drops to the ground, see `step`). */
  freeze(): void {
    this.flying = false;
    this.vy = 0;
  }

  /**
   * Age the player: 0 = start of life … 1 = retirement. Visible (artwork per age, see
   * `life.looks`) and in the agility (weaker impulse → lower hops).
   */
  setLifeProgress(progress: number): void {
    this.agility = byAging(1, L.agilityOld, agingAt(progress));
    const age = L.startAge + (L.endAge - L.startAge) * progress;
    let look = 0;
    while (look + 1 < L.looks.length && age >= L.looks[look + 1].fromAge) look++;
    if (look === this.look) return;
    const first = this.look < 0;
    this.look = look;
    this.setTexture(L.looks[look].texture, this.frameIndex);
    this.setDisplaySize(this.ws.px(T.displayWidth), this.ws.px(T.displayHeight));
    if (first) return;
    // birthday pop when the look changes
    const sx = this.scaleX;
    const sy = this.scaleY;
    this.scene.tweens.add({ targets: this, scaleX: sx * 1.25, scaleY: sy * 1.25, duration: 120, yoyo: true, ease: 'Quad.Out' });
  }

  /** Show a reaction for a moment: impact after a hit (wins over joy), joy after a coin. */
  react(kind: Reaction): void {
    if (kind === 'joy' && this.reaction === 'hit') return;
    this.reaction = kind;
    this.reactionSec = kind === 'hit' ? A.hitSec : A.joySec;
    this.showFrame(PLAYER_FRAME[kind]);
  }

  /** A full life: the fox cheers as soon as it is on the ground (until the next `reset`). */
  cheer(): void {
    this.cheering = true;
  }

  /** Blink for `sec` seconds (invulnerable after a hit). */
  blink(sec: number): void {
    this.blinkSec = sec;
  }

  /** Idle animation while waiting for the first tap: hover on the spot. */
  bob(dtSec: number): void {
    this.bobTime += dtSec;
    this.y = this.baseY + this.ws.px(Math.sin(this.bobTime * T.bobSpeed) * T.bobAmplitude);
    this.setAngle(0);
    this.loop(PLAYER_FRAME.float, A.floatFps, dtSec);
  }

  /** Physics and animation step. `floorY` is the pixel y of the centre resting on the ground (the floor is safe). */
  step(dtSec: number, floorY: number): void {
    if (this.blinkSec > 0) {
      this.blinkSec = Math.max(0, this.blinkSec - dtSec);
      this.setAlpha(this.blinkSec > 0 && Math.floor(this.blinkSec * 12) % 2 === 0 ? 0.3 : 1);
    }
    if (!this.flying) return;

    this.vy = Math.min(this.vy + T.gravity * dtSec, T.maxFallSpeed);
    this.y += this.ws.px(this.vy * dtSec);

    // keep the whole figure on screen at the top
    const top = this.ws.px(T.displayHeight / 2);
    if (this.y < top) {
      this.y = top;
      if (this.vy < 0) this.vy = 0;
    }
    const onGround = this.y >= floorY;
    if (onGround) {
      this.y = floorY;
      if (this.vy > 0) this.vy = 0;
    }
    const t = onGround ? 0 : this.vy < 0 ? this.vy / T.flapVelocity : this.vy / T.maxFallSpeed;
    const target = t < 0 ? -t * T.angleUp : t * T.angleDown;
    this.angle = Phaser.Math.Linear(this.angle, target, Math.min(1, dtSec * 10));

    this.kickSec = Math.max(0, this.kickSec - dtSec);
    this.reactionSec = Math.max(0, this.reactionSec - dtSec);
    if (this.reactionSec === 0) this.reaction = null;
    if (onGround && this.cheering) {
      this.angle = 0;
      this.showFrame(PLAYER_FRAME.cheer);
    } else if (this.reaction) this.showFrame(PLAYER_FRAME[this.reaction]);
    else if (this.kickSec > 0) this.showFrame(PLAYER_FRAME.hop);
    else if (onGround) this.showFrame(PLAYER_FRAME.float[0]);
    else if (this.vy < A.riseBelowVy * this.agility) this.showFrame(PLAYER_FRAME.hop);
    else if (this.vy > A.fallAboveVy) this.showFrame(PLAYER_FRAME.fall);
    else this.loop(PLAYER_FRAME.float, A.floatFps, dtSec);
  }

  /** Play a looping frame sequence. */
  private loop(frames: readonly number[], fps: number, dtSec: number): void {
    this.animTime += dtSec;
    this.showFrame(frames[Math.floor(this.animTime * fps) % frames.length]);
  }

  private showFrame(frame: number): void {
    if (frame === this.frameIndex) return;
    this.frameIndex = frame;
    this.setFrame(frame, false, false);
  }

  /** Air puff below the figure – makes the impulse of a tap visible (dark outline: readable on the light sky). */
  private puff(): void {
    const px = (wu: number) => this.ws.px(wu);
    const y = this.y + this.footOffset * 0.8;
    const n = 6;
    for (let i = 0; i < n; i++) {
      const size = Math.max(2, Math.round(px(Phaser.Math.Between(10, 15))));
      const dir = (i / (n - 1)) * 2 - 1; // -1 … 1
      const p = this.scene.add
        .rectangle(this.x - px(10) + dir * px(18), y, size, size, i % 2 ? 0xcfe4ff : 0xffffff)
        .setStrokeStyle(Math.max(1, px(3)), PALETTE.ink)
        .setDepth(this.depth - 1);
      this.scene.tweens.add({
        targets: p,
        x: p.x + dir * px(40) - px(28),
        y: p.y + px(Phaser.Math.Between(24, 46)),
        alpha: 0,
        scale: 0.4,
        duration: 420,
        ease: 'Quad.Out',
        onComplete: () => p.destroy(),
      });
    }
  }
}
